import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MapPin,
  Truck,
  RefreshCw,
  Loader,
  AlertCircle,
  Clock,
  Navigation,
  ExternalLink,
  Maximize2,
  Minimize2,
  X,
  Calendar,
} from 'lucide-react';
import axios from 'axios';
import { getDevices, getPositions, knotsToKmh } from '../services/traccarService';
import { getAddressesByVorgangsnummern } from '../services/pipedriveService';
import { format } from 'date-fns';

const POLL_INTERVAL_MS = 10000;
const DEFAULT_MAP_CENTER = { lat: 51.1657, lng: 10.4515 };
const DEFAULT_ZOOM = 6;

const SERVICEPROVIDER_API_URL = process.env.REACT_APP_SERVICEPROVIDER_API_URL || 'https://lolworlds.online/api/serviceprovider/getServiceprovider';
const SFS_API_TOKEN = process.env.REACT_APP_SFS_API_TOKEN;

const createApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  if (cleanBaseUrl.endsWith('/api')) {
    const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
    return `${cleanBaseUrl}/${endpointWithoutApi}`;
  }
  return `${cleanBaseUrl}/${cleanEndpoint}`;
};

/** Normalize string for matching (license plate / device name). */
const normalizeForMatch = (s) => {
  if (s == null || typeof s !== 'string') return '';
  return s.trim().toUpperCase().replace(/\s+/g, ' ');
};

const speedKmh = (pos) => (pos?.speed != null ? Math.round(knotsToKmh(pos.speed)) : null);

/** Nur Termine mit Start !== Ende (Platzhalter wie 22:51–22:51 ausblenden). */
const isRealTermin = (t) => {
  if (!t || typeof t !== 'object') return false;
  const start = String(t.Startzeit ?? t.startzeit ?? t.start ?? '').trim();
  const end = String(t.Endzeit ?? t.endzeit ?? t.end ?? '').trim();
  return start !== end;
};

/** Formatiert einen Stressfrei-Termin für Anzeige (Start–Ende, optional Art). */
const formatTerminLine = (t) => {
  const start = t.Startzeit ?? t.startzeit ?? t.start ?? t.Start ?? '';
  const end = t.Endzeit ?? t.endzeit ?? t.end ?? t.End ?? '';
  const type = t.Terminart ?? t.terminart ?? t.type ?? '';
  const timePart = [start, end].filter(Boolean).join(' – ');
  return timePart ? (type ? `${timePart} ${type}` : timePart) : (type || 'Termin');
};

/** Parst Datum + Zeit-String zu Date (z. B. "2026-02-21" + "08:00:00"). */
const parseTerminDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const date = String(dateStr).trim().split('T')[0];
  const time = String(timeStr).trim().substring(0, 8); // "HH:mm:ss" oder "HH:mm"
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Phase-1-Monitoring: Ermittelt aktuellen und nächsten Termin (Zeiten + Adressen aus Pipedrive-Match).
 * @param {Array} termine - Gefilterte Termine (evtl. mit originAddress/destinationAddress angereichert)
 * @param {Date} now - Aktuelle Zeit
 * @returns {{ currentEnd, nextStart, nextArt, nextAddress }}
 */
const getCurrentAndNextTermin = (termine, now) => {
  const result = { currentEnd: null, nextStart: null, nextArt: null, nextAddress: null };
  if (!Array.isArray(termine) || termine.length === 0) return result;

  const todayStr = format(now, 'yyyy-MM-dd');
  const withDates = termine
    .map((t) => {
      const dateStr = t.Datum ?? t.datum ?? todayStr;
      const start = parseTerminDateTime(dateStr, t.Startzeit ?? t.startzeit ?? t.start);
      const end = parseTerminDateTime(dateStr, t.Endzeit ?? t.endzeit ?? t.end);
      const art = t.Terminart ?? t.terminart ?? t.type ?? '';
      if (!start || !end) return null;
      return { start, end, art, raw: t };
    })
    .filter(Boolean)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const current = withDates.find((t) => now >= t.start && now <= t.end);
  if (current) {
    result.currentEnd = format(current.end, 'HH:mm');
  }

  const refTime = current ? current.end : now;
  const next = withDates.find((t) => t.start > refTime);
  if (next) {
    result.nextStart = format(next.start, 'HH:mm');
    result.nextArt = next.art || null;
    const r = next.raw || {};
    const addr = r.destinationAddress || r.originAddress || '';
    if (addr) result.nextAddress = addr;
  }

  return result;
};

const escapeHtml = (s) => {
  if (s == null) return '';
  const str = String(s);
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
};

/** Formatiert deviceTime für Popup-Anzeige (z. B. 21.02.2026, 12:58). */
const formatDeviceTimeForPopup = (deviceTime) => {
  if (!deviceTime) return '';
  try {
    const d = new Date(deviceTime);
    return isNaN(d.getTime()) ? String(deviceTime) : format(d, 'dd.MM.yyyy, HH:mm');
  } catch {
    return String(deviceTime);
  }
};

/** Baut das HTML für die Karten-Infobox (übersichtlich, mit inline-Styles). */
const buildInfoWindowContent = (name, pos, termineLines, monitoring) => {
  const speed = speedKmh(pos);
  const standStr = formatDeviceTimeForPopup(pos?.deviceTime);
  const base = 'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:13px;color:#1f2937;';
  const header = 'font-weight:700;font-size:15px;color:#111827;margin:0 0 10px 0;padding-bottom:8px;border-bottom:1px solid #e5e7eb;';
  const row = 'margin:4px 0;display:flex;align-items:center;gap:6px;';
  const label = 'color:#6b7280;min-width:72px;';
  const sectionTitle = 'font-weight:600;color:#374151;margin:10px 0 6px 0;font-size:12px;text-transform:uppercase;letter-spacing:0.02em;';
  const listBg = 'background:#f8fafc;border-radius:6px;padding:8px 10px;margin-top:4px;';
  const listItem = 'padding:4px 0;border-bottom:1px solid #e2e8f0;font-size:12px;color:#475569;';
  const listItemLast = 'padding:4px 0;font-size:12px;color:#475569;';

  let monitoringBlock = '';
  if (monitoring && (monitoring.currentEnd || monitoring.nextStart)) {
    const parts = [];
    if (monitoring.currentEnd) parts.push(`Aktuell bis ${escapeHtml(monitoring.currentEnd)}`);
    if (monitoring.nextStart) parts.push(`Nächster ${escapeHtml(monitoring.nextStart)}${monitoring.nextArt ? ' · ' + escapeHtml(monitoring.nextArt) : ''}`);
    const line1 = parts.join(' · ');
    const line2 = monitoring.nextAddress ? `<div style="margin-top:4px;font-size:11px;color:#64748b">${escapeHtml(monitoring.nextAddress)}</div>` : '';
    monitoringBlock = `<div style="${sectionTitle}">Monitoring</div><div style="${listBg}"><div style="${listItemLast}">${line1}${line2}</div></div>`;
  }

  let termineBlock = '';
  if (termineLines.length > 0) {
    const items = termineLines
      .map((line, i) => {
        const style = i < termineLines.length - 1 ? listItem : listItemLast;
        return `<div style="${style}">${escapeHtml(line)}</div>`;
      })
      .join('');
    termineBlock = `<div style="${sectionTitle}">Termine heute</div><div style="${listBg}">${items}</div>`;
  }

  return `
<div style="${base}min-width:240px;max-width:320px;padding:0 16px 14px 16px;">
  <h3 style="${header}">${escapeHtml(name)}</h3>
  <div style="${row}"><span style="${label}">Geschwindigkeit</span><strong>${speed != null ? `${speed} km/h` : '–'}</strong></div>
  <div style="${row}"><span style="${label}">Letzter Stand</span><span>${standStr || '–'}</span></div>
  ${monitoringBlock}
  ${termineBlock}
</div>`;
};

const FleetLiveTracking = () => {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState({});
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [stressfreiTermineByVehicle, setStressfreiTermineByVehicle] = useState({});
  const [vorgangsnummerToAddress, setVorgangsnummerToAddress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [map, setMap] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [monitoringTick, setMonitoringTick] = useState(0); // alle 60s hochzählen für frisches "now"
  const markersRef = useRef({});
  const mapContainerRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Karte nach Vergrößern/Verkleinern neu zeichnen (Resize)
  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    const timer = setTimeout(() => {
      window.google.maps.event.trigger(map, 'resize');
    }, 100);
    return () => clearTimeout(timer);
  }, [map, mapExpanded]);

  const fetchDevices = useCallback(async () => {
    try {
      const list = await getDevices();
      setDevices(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Traccar getDevices:', e);
      setError(e.message || 'Geräte konnten nicht geladen werden.');
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const list = await getPositions();
      if (!Array.isArray(list)) return;
      const next = {};
      list.forEach((p) => {
        if (p.deviceId != null) next[p.deviceId] = p;
      });
      setPositions((prev) => ({ ...prev, ...next }));
      setLastUpdate(new Date());
    } catch (e) {
      console.warn('Traccar getPositions:', e);
    }
  }, []);

  const fetchFleetVehicles = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const url = createApiUrl('api/vehicles');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFleetVehicles(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.warn('Fleet vehicles:', e);
    }
  }, []);

  const fetchStressfreiVehiclesWithTermine = useCallback(async () => {
    if (!SFS_API_TOKEN) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    try {
      const res = await axios.post(SERVICEPROVIDER_API_URL, {
        date_from: today,
        date_to: today,
        joins: ['Vertraege', 'Eigenschaften', 'Termine'],
      }, {
        headers: { accept: 'application/json', 'Content-Type': 'application/json', Authorization: SFS_API_TOKEN },
      });
      if (!res.data || !Array.isArray(res.data)) return;
      const map = {};
      res.data
        .filter((item) => {
          const eig = item.Eigenschaften || {};
          return eig['staff_status_Fuhrpark_Maschinen '] === null;
        })
        .forEach((item) => {
          const emp = item.Teammitglied || item;
          const id = String(emp.Id || emp.id || emp.personalid || emp.uuid || '');
          const name = emp.name || `${emp.Vorname || emp.vorname || ''} ${emp.Nachname || emp.nachname || ''}`.trim() || 'Unbekannt';
          const rawTermine = item.Termine || emp.Termine || [];
          const termine = rawTermine.filter(isRealTermin);
          if (id) map[id] = { name, termine };
        });
      setStressfreiTermineByVehicle(map);
    } catch (e) {
      console.warn('Stressfrei vehicles/termine:', e);
    }
  }, []);

  // Pipedrive: Vorgangsnummern aus allen Terminen sammeln → Deals matchen → Adress-Map
  const fetchVorgangsnummerAddresses = useCallback(async (termineByVehicle) => {
    const vorgangsnummern = new Set();
    Object.values(termineByVehicle || {}).forEach(({ termine }) => {
      (termine || []).forEach((t) => {
        const vn = (t.vorgangsno ?? t.Vorgangsno ?? '').toString().trim();
        if (vn) vorgangsnummern.add(vn);
      });
    });
    if (vorgangsnummern.size === 0) {
      setVorgangsnummerToAddress({});
      return;
    }
    try {
      const map = await getAddressesByVorgangsnummern(Array.from(vorgangsnummern));
      setVorgangsnummerToAddress(Object.fromEntries(map));
    } catch (e) {
      console.warn('Pipedrive Vorgangsnummer→Adresse:', e);
      setVorgangsnummerToAddress({});
    }
  }, []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchDevices(),
        fetchPositions(),
        fetchFleetVehicles(),
        fetchStressfreiVehiclesWithTermine(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchDevices, fetchPositions, fetchFleetVehicles, fetchStressfreiVehiclesWithTermine]);

  // Sobald Stressfrei-Termine da sind: Vorgangsnummern sammeln → Pipedrive matchen → Adress-Map setzen
  useEffect(() => {
    if (Object.keys(stressfreiTermineByVehicle).length === 0) return;
    fetchVorgangsnummerAddresses(stressfreiTermineByVehicle);
  }, [stressfreiTermineByVehicle, fetchVorgangsnummerAddresses]);

  useEffect(() => {
    loadInitial();
    pollTimerRef.current = setInterval(fetchPositions, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [loadInitial, fetchPositions]);

  /** Für jedes Traccar-Gerät: zugeordnetes Stressfrei-Fahrzeug und Termine (heute), mit Adressen aus Pipedrive (Vorgangsnummer-Match). */
  const deviceToVehicleInfo = useMemo(() => {
    const out = {};
    const devList = Array.isArray(devices) ? devices : [];
    devList.forEach((device) => {
      const deviceId = device.id;
      const normUnique = normalizeForMatch(device.uniqueId || '');
      let vehicle = fleetVehicles.find((v) => v.traccar_device_id != null && Number(v.traccar_device_id) === Number(deviceId));
      if (!vehicle && normUnique) {
        vehicle = fleetVehicles.find((v) => v.traccar_unique_id && normalizeForMatch(v.traccar_unique_id) === normUnique);
      }
      if (!vehicle) {
        const normName = normalizeForMatch(device.name || '');
        vehicle = fleetVehicles.find(
          (v) =>
            (v.license_plate && normalizeForMatch(v.license_plate) === normName) ||
            (v.license_plate && normUnique && normalizeForMatch(v.license_plate) === normUnique)
        );
      }
      const stressfreiId = vehicle?.stressfrei_id ? String(vehicle.stressfrei_id) : null;
      const sf = stressfreiId ? stressfreiTermineByVehicle[stressfreiId] : null;
      const rawTermine = sf?.termine || [];
      const termine = rawTermine.map((t) => {
        const vn = (t.vorgangsno ?? t.Vorgangsno ?? '').toString().trim();
        const addr = vorgangsnummerToAddress[vn] || {};
        return {
          ...t,
          originAddress: addr.originAddress || t.originAddress,
          destinationAddress: addr.destinationAddress || t.destinationAddress,
        };
      });
      out[deviceId] = {
        displayName: (vehicle?.license_plate || sf?.name) || (device.name || device.uniqueId || `Gerät ${device.id}`),
        vehicle,
        termine,
      };
    });
    return out;
  }, [devices, fleetVehicles, stressfreiTermineByVehicle, vorgangsnummerToAddress]);

  /** Phase-1-Monitoring: pro Gerät aktueller Termin (bis HH:mm) und nächster Termin (ab HH:mm). */
  const deviceMonitoring = useMemo(() => {
    const now = new Date();
    const out = {};
    Object.keys(deviceToVehicleInfo).forEach((deviceId) => {
      const info = deviceToVehicleInfo[deviceId];
      out[deviceId] = getCurrentAndNextTermin(info?.termine || [], now);
    });
    return out;
  }, [deviceToVehicleInfo, monitoringTick]);

  // Google Maps laden wie im TourPlanner (gleicher Script-Load)
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      setMapsReady(true);
      return;
    }
    if (window.google?.maps) {
      setMapsReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => setMapsReady(true);
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // Karte wie in Traccar: Straßenkarte, Zoom +/-, Maßstabsleiste
  useEffect(() => {
    if (!mapsReady || !window.google?.maps) return;
    const el = document.getElementById('fleet-live-map');
    if (!el) return;
    const mapInstance = new window.google.maps.Map(el, {
      zoom: DEFAULT_ZOOM,
      center: DEFAULT_MAP_CENTER,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_TOP },
      scaleControl: true,
    });
    setMap(mapInstance);
    return () => setMap(null);
  }, [mapsReady, loading]);

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasAny = false;

    const labelText = (device) => {
      const name = (device.name || device.uniqueId || '').toString().trim();
      if (name.length) return name.charAt(0).toUpperCase();
      return String(device.id).charAt(0) || '?';
    };

    devices.forEach((device) => {
      const pos = positions[device.id];
      if (!pos || pos.latitude == null || pos.longitude == null) return;
      hasAny = true;
      const latLng = { lat: pos.latitude, lng: pos.longitude };
      bounds.extend(latLng);

      const info = deviceToVehicleInfo[device.id];
      const name = info?.displayName ?? device.name ?? device.uniqueId ?? `Gerät ${device.id}`;
      const termineLines = (info?.termine || []).slice(0, 8).map(formatTerminLine);
      const monitoring = deviceMonitoring[device.id];
      const infoContent = buildInfoWindowContent(name, pos, termineLines, monitoring);

      if (!markersRef.current[device.id]) {
        const marker = new window.google.maps.Marker({
          map,
          position: latLng,
          title: name,
          label: {
            text: labelText(device),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px',
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#15803d',
            strokeWeight: 2,
          },
        });
        const info = new window.google.maps.InfoWindow({ content: infoContent });
        marker.addListener('click', () => info.open(map, marker));
        markersRef.current[device.id] = { marker, info };
      } else {
        markersRef.current[device.id].marker.setPosition(latLng);
        markersRef.current[device.id].marker.setLabel(labelText(device));
        markersRef.current[device.id].info.setContent(infoContent);
      }
    });

    Object.keys(markersRef.current).forEach((id) => {
      if (!devices.some((d) => String(d.id) === String(id))) {
        markersRef.current[id].marker.setMap(null);
        markersRef.current[id].info?.close();
        delete markersRef.current[id];
      }
    });

    if (hasAny && bounds.getNorthEast().lat() !== bounds.getSouthWest().lat()) {
      map.fitBounds(bounds, { top: 100, right: 60, bottom: 60, left: 320 });
    }
  }, [map, devices, positions, deviceToVehicleInfo, deviceMonitoring]);

  const handleRefresh = () => {
    setError(null);
    loadInitial();
  };

  const openInGoogleMaps = (lat, lng) => {
    if (lat == null || lng == null) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const focusMapOnDevice = (device) => {
    const pos = positions[device.id];
    if (!map || !pos || pos.latitude == null || pos.longitude == null) return;
    const latLng = { lat: pos.latitude, lng: pos.longitude };
    map.panTo(latLng);
    map.setZoom(15);
    const entry = markersRef.current[device.id];
    if (entry?.marker && entry?.info) {
      entry.info.open(map, entry.marker);
    }
  };

  const formatTime = (deviceTime) => {
    if (!deviceTime) return '–';
    try {
      const d = new Date(deviceTime);
      return isNaN(d.getTime()) ? deviceTime : format(d, 'dd.MM. HH:mm');
    } catch {
      return deviceTime;
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="h-8 w-8 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Lade Traccar-Daten...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            Live-Standorte
          </h2>
          <span className="text-xs text-gray-500">
            Aktualisierung alle {POLL_INTERVAL_MS / 1000}s
            {lastUpdate && ` · Letzte: ${format(lastUpdate, 'HH:mm:ss')}`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMapExpanded(true)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Karte vergrößern"
          >
            <Maximize2 className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Aktualisieren"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex-1 min-h-[520px] flex gap-4">
        <div className="w-72 flex-shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200 font-medium text-gray-700">
            Fahrzeuge ({devices.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {devices.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center">
                Keine Geräte in Traccar gefunden.
              </div>
            ) : (
              devices.map((device) => {
                const pos = positions[device.id];
                const hasPosition = pos && pos.latitude != null && pos.longitude != null;
                const info = deviceToVehicleInfo[device.id];
                const monitoring = deviceMonitoring[device.id];
                const name = info?.displayName ?? device.name ?? device.uniqueId ?? `Gerät ${device.id}`;
                const termine = info?.termine ?? [];
                const hasMonitoring = monitoring && (monitoring.currentEnd || monitoring.nextStart);
                return (
                  <div
                    key={device.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => hasPosition && focusMapOnDevice(device)}
                    onKeyDown={(e) => hasPosition && (e.key === 'Enter' || e.key === ' ') && focusMapOnDevice(device)}
                    className={`border-b border-gray-100 p-3 transition-colors ${hasPosition ? 'cursor-pointer hover:bg-blue-50 hover:border-l-4 hover:border-l-blue-400 border-l-4 border-l-transparent' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <Truck className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{name}</div>
                        {hasMonitoring && (
                          <div className="text-xs text-indigo-700 mt-1 flex flex-col gap-0.5">
                            <span className="flex items-center gap-1 flex-wrap">
                              <Clock className="h-3 w-3 flex-shrink-0" />
                              {monitoring.currentEnd && `Aktuell bis ${monitoring.currentEnd}`}
                              {monitoring.currentEnd && monitoring.nextStart && ' · '}
                              {monitoring.nextStart && `Nächster ${monitoring.nextStart}`}
                              {monitoring.nextStart && monitoring.nextArt && ` · ${monitoring.nextArt}`}
                            </span>
                            {monitoring.nextAddress && (
                              <span className="text-gray-600 truncate pl-4" title={monitoring.nextAddress}>{monitoring.nextAddress}</span>
                            )}
                          </div>
                        )}
                        {termine.length > 0 && (
                          <div className="text-xs text-amber-700 mt-1 flex items-center gap-1 flex-wrap">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            <span>{termine.length} Termin{termine.length !== 1 ? 'e' : ''} heute</span>
                            <div className="w-full pl-4 mt-0.5 space-y-0.5">
                              {termine.slice(0, 3).map((t, i) => (
                                <div key={i} className="text-gray-600">{formatTerminLine(t)}</div>
                              ))}
                              {termine.length > 3 && <div className="text-gray-500">… +{termine.length - 3}</div>}
                            </div>
                          </div>
                        )}
                        {hasPosition ? (
                          <>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(pos.deviceTime)}
                            </div>
                            {speedKmh(pos) != null && speedKmh(pos) > 0 && (
                              <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                                <Navigation className="h-3 w-3" />
                                {speedKmh(pos)} km/h
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openInGoogleMaps(pos.latitude, pos.longitude);
                              }}
                              className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              In Google Maps öffnen
                            </button>
                          </>
                        ) : (
                          <div className="text-xs text-gray-400 mt-1">Keine Position</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div
          className={
            mapExpanded
              ? 'fixed inset-0 z-50 flex flex-col bg-white'
              : 'flex-1 min-w-0 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 h-[480px]'
          }
        >
          {mapExpanded && (
            <div className="flex flex-nowrap items-center justify-between gap-3 w-full px-4 py-2 border-b border-gray-200 bg-white shrink-0">
              <span className="text-sm font-medium text-gray-700 truncate min-w-0">Karte vergrößert</span>
              <button
                type="button"
                onClick={() => setMapExpanded(false)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 shrink-0"
                title="Karte verkleinern"
              >
                <Minimize2 className="h-5 w-5" />
                <span className="text-sm whitespace-nowrap">Schließen</span>
              </button>
            </div>
          )}
          <div
            id="fleet-live-map"
            className={`w-full rounded-lg ${mapExpanded ? 'flex-1 min-h-0' : 'h-full'}`}
            ref={mapContainerRef}
          />
        </div>
      </div>
    </div>
  );
};

export default FleetLiveTracking;
